import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import ExpertSection from "@/components/ExpertSection";

import { Shield, Check, AlertCircle, Building2, Users, Home, HeartPulse, FileText, HardHat, UserCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";

const Betriebshaftpflicht = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="relative z-10">
        <Navigation />
      
      {/* Breadcrumbs */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/weitere-versicherungen">Weitere Versicherungen</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Betriebshaftpflicht</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Hero Section */}
      <ResponsiveSection spacing="lg">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-accent text-background">Wichtige Absicherung</Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 break-words">
            Betriebshaftpflicht für Arztpraxen
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Schutz bei Personen- und Sachschäden durch den Praxisbetrieb – unabhängig von der medizinischen Behandlung
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">24/7</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Schutz</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">bis 10 Mio.€</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Deckung</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">Mitarbeiter</div>
              <div className="text-xs sm:text-sm text-muted-foreground">mitversichert</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Check className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">Rechtsschutz</div>
              <div className="text-xs sm:text-sm text-muted-foreground">inklusive</div>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* Leistungsübersicht */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
          Leistungsübersicht
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Umfassender Schutz für alle betrieblichen Risiken
        </p>

        <div className="glas-cards grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">
          {/* Versicherte Risiken */}
          <div className="relative pb-6 md:pb-0">
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                <AlertCircle className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                  Versicherte Risiken
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                Schutz bei betrieblichen Schadensfällen
              </p>
              
              <div className="list-container">
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Sturzunfälle im Wartezimmer oder Praxisräumen</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Beschädigung von Patienteneigentum (z.B. Brille, Kleidung)</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Schäden durch defekte Praxisausstattung</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Schäden durch Reinigungspersonal oder Mitarbeiter</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Umweltschäden durch Praxisbetrieb</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Schlüsselverlust von gemieteten Räumen</span>
                </div>
              </div>
            </div>
          </div>

          {/* Deckungsumfang */}
          <div className="relative">
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                <FileText className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                  Deckungsumfang
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                Was die Versicherung leistet
              </p>
              
              <div className="list-container">
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Personenschäden bis zur Deckungssumme</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Sachschäden und Vermögensschäden</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Umwelthaftpflichtschäden</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Mietsachschäden an Praxisräumen</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Passiver Rechtsschutz gegen unberechtigte Forderungen</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Anwalts- und Gerichtskosten</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Besondere Vorteile */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Besondere Vorteile
        </h2>

        <div className="glas-cards">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Mitarbeiter mitversichert</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Alle Mitarbeiter automatisch mitversichert - von MFA bis Reinigungskraft</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Mietsachschäden</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Schäden an gemieteten Praxisräumen mitversichert</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Umwelthaftpflicht</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Schutz bei Umweltschäden durch Heizöltanks und Klimaanlagen</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Forderungsausfalldeckung</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Springt ein, wenn ein Schädiger nicht zahlen kann</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Schlüsselverlust</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Kostenübernahme bei Verlust von Praxis- oder Gebäudeschlüsseln</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Gefälligkeitsschäden</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Auch unentgeltliche Gefälligkeiten mitversichert</p>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Für wen geeignet */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center break-words">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-primary">
            Für wen ist diese Versicherung geeignet?
          </span>
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Unverzichtbar für jeden Praxisbetrieb
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="card-glass">
            <CardContent className="pt-6">
              <Home className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Einzelpraxen</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Hausarztpraxen</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Facharztpraxen</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Zahnarztpraxen</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Gemeinschaftspraxen</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Berufsausübungsgemeinschaften</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Praxisgemeinschaften</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Ärztehaus-Gemeinschaften</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">MVZ & Kliniken</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Medizinische Versorgungszentren</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Tageskliniken</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Ambulante OP-Zentren</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* Schadensbeispiele */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
          Echte Schadensbeispiele
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Reale Fälle zeigen die Notwendigkeit
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Schadenhöhe: 85.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Sturzunfall im Wartezimmer</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Patientin stürzte über ein loses Kabel und brach sich den Oberschenkel. Langwierige Behandlung und Reha notwendig.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vollständig übernommen</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Schadenhöhe: 12.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Beschädigung Patientenbrille</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                MFA ließ versehentlich teure Designerbrille fallen. Ersatz der Brille und Folgekosten durch Sehbeeinträchtigung.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vollständig übernommen</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Schadenhöhe: 45.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Wasserschaden beim Nachbarn</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Defekte Wasserleitung in der Praxis verursachte schweren Wasserschaden in darunter liegender Wohnung.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Inkl. Sanierungskosten</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Schadenhöhe: 8.500 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Schlüsselverlust Praxisgebäude</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Verlust des Generalschlüssels machte Austausch der gesamten Schließanlage des Gebäudes notwendig.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vollständig übernommen</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* CTA Section */}
      <ResponsiveSection spacing="lg">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 text-foreground">
            Jetzt persönliche Beratung vereinbaren
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
            Als spezialisierter Versicherungsmakler für Ärzte unterstütze ich Sie bei der Auswahl der passenden Betriebshaftpflicht.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90">
            <Link to="/termin-buchen">Beratungstermin buchen</Link>
          </Button>
        </div>
      </ResponsiveSection>

      <ExpertSection />
      <Footer />
      </div>
    </div>
  );
};

export default Betriebshaftpflicht;
